// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngSticky from "@ces/App/Components/XngSticky";
import { mergeWithCss } from "@ces/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useMemo, useState } from "react";

interface CartPageTemplateProps {
    children: React.ReactNode;
    extendedStyles?: CartPageTemplateStyles;
    isCheckout?: boolean;
}

interface Props {
    MainSection: React.FC<any>;
    HeaderSection: React.FC<any>;
    TitleSection: React.FC<any>;
    CartApproverMessage: React.FC<any>;
    CartFulfillmentSelector: React.FC<any>;
    CartTotalSection: React.FC<any>;
    ActionsSection: React.FC<any>;
}

interface CartPageTemplateContextProps {
    styles: CartPageTemplateStyles;
    isCheckout?: boolean;
}

export interface CartPageTemplateStyles {
    container?: InjectableCss;
    headerSection?: InjectableCss;
    titleSection?: InjectableCss;
    mainSection?: InjectableCss;
    cartApproverMessage?: InjectableCss;
    cartFulfullmentSelector?: InjectableCss;
    totalSection?: InjectableCss;
    actionsSection?: InjectableCss;
    xngStickyStyles?: InjectableCss;
}

const CartPageTemplateContext = React.createContext<CartPageTemplateContextProps>({
    styles: {},
    isCheckout: false,
});

export const useCartPageTemplateContext = () => {
    const context = React.useContext(CartPageTemplateContext);
    if (!context) {
        throw new Error("CartPageTemplate compound components cannot be rendered outside CartPageTemplate");
    }
    return context;
};

export const cartPageTemplateBreakpoint = 820;
export const cartPageTemplateStyles: CartPageTemplateStyles = {};

const CartPageTemplate: FC<CartPageTemplateProps> & Props = ({ children, extendedStyles, isCheckout }) => {
    const [styles] = useState(() => mergeWithCss(cartPageTemplateStyles, extendedStyles));
    const value = useMemo(() => ({ styles, isCheckout }), [styles, isCheckout]);
    return (
        <CartPageTemplateContext.Provider value={value} >
            <StyledWrapper {...styles.container}>{children}</StyledWrapper>
        </CartPageTemplateContext.Provider>
    );
};

const HeaderSection: FC = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.headerSection}>{children}</StyledWrapper>;
};

const TitleSection: FC = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.titleSection}>{children}</StyledWrapper>;
};

const CartApproverMessage: FC = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.cartApproverMessage}>{children}</StyledWrapper>;
};
const CartFulfillmentSelector: FC = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.cartFulfullmentSelector}>{children}</StyledWrapper>;
};
const MainSection: FC = ({ children }) => {
    const { styles, isCheckout } = useCartPageTemplateContext();
    return (
        <StyledWrapper {...styles.mainSection} {...{ isCheckout }}>
            {children}
        </StyledWrapper>
    );
};

const CartTotalSection: FC = ({ children }) => {
    const { styles, isCheckout } = useCartPageTemplateContext();

    return (
        <StyledWrapper {...styles.totalSection} {...{ isCheckout }}>
            <XngSticky {...styles.xngStickyStyles}>{children}</XngSticky>
        </StyledWrapper>
    );
};

const ActionsSection: FC = ({ children }) => {
    const { styles } = useCartPageTemplateContext();
    return <StyledWrapper {...styles.actionsSection}>{children}</StyledWrapper>;
};

CartPageTemplate.HeaderSection = HeaderSection;
CartPageTemplate.MainSection = MainSection;
CartPageTemplate.TitleSection = TitleSection;
CartPageTemplate.CartApproverMessage = CartApproverMessage;
CartPageTemplate.CartFulfillmentSelector = CartFulfillmentSelector;
CartPageTemplate.CartTotalSection = CartTotalSection;
CartPageTemplate.ActionsSection = ActionsSection;

export default CartPageTemplate;
